// Chat category enum
export enum ChatCategory {
  GENERAL = "GENERAL",
  HTS_CLASSIFICATION = "HTS_CLASSIFICATION",
  CBP_AUDIT = "CBP_AUDIT",
}

// Message sender enum - unified format
export enum MessageSender {
  USER = "USER",
  AI = "AI"
}

// Context interface for type safety
export interface ChatContext {
  [key: string]: unknown;
}

// Frontend input types
export interface ChatCreateInput {
  title: string;
  clientId?: string;
  tenantId?: string;
  category?: ChatCategory;
  context?: ChatContext;
  sessionId?: string;
}

export interface MessageCreateInput {
  content: string;
  sender: MessageSender;
  context?: ChatContext;
}

export interface ChatMessageInput {
  content: string;
  source: MessageSender; // Now using unified enum
  context?: ChatContext;
  attachmentIds?: string[]; // IDs of uploaded chat attachments
}

// Chat attachment status enum
export enum ChatAttachmentStatus {
  UPLOADED = "UPLOADED",
  PROCESSING = "PROCESSING",
  READY = "READY",
  FAILED = "FAILED",
}

// Chat attachment interface
export interface ChatAttachment {
  _id: string;
  chatId: string;
  tenantId: string;
  createdBy: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  s3Key: string;
  status: ChatAttachmentStatus;
  error?: {
    code?: string;
    message?: string;
  };
  extractedText?: string;
  summary?: string;
  meta?: {
    pageCount?: number;
    rowCount?: number;
    colCount?: number;
    sheetCount?: number;
    encoding?: string;
    width?: number;
    height?: number;
    sha256?: string;
  };
  ingestedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Chat attachment input for creating
export interface CreateChatAttachmentInput {
  chatId: string;
  tenantId?: string;
  s3Key: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

// Chat attachment response types
export interface ChatAttachmentResponse {
  data: ChatAttachment;
}

export interface ChatAttachmentsResponse {
  data: ChatAttachment[];
}

export interface GenerateChatAttachmentSignedUrlResponse {
  generateChatAttachmentUploadSignedUrl: {
    signedUrl: string;
    key: string;
  };
}

export interface CreateChatAttachmentResponse {
  createChatAttachment: ChatAttachmentResponse;
}

export interface GetChatAttachmentsResponse {
  chatAttachments: ChatAttachmentsResponse;
}

export interface GetChatAttachmentResponse {
  chatAttachment: ChatAttachmentResponse;
}

export interface DeleteChatAttachmentResponse {
  deleteChatAttachment: {
    success: boolean;
  };
}

export interface ChatFilterInput {
  category?: ChatCategory;
  clientId?: string;
  pageNo?: number;
  pageSize?: number;
  isActive?: boolean;
}

// Structured answer envelope produced by the agent's `finalize` tool.
// The chat-advisor renderer reads question / final_hts_code / rulings
// directly from here; nothing depends on parsing markdown for structure.
export interface FinalizeArgs {
  kind:
    | "clarifying_question"
    | "final_classification"
    | "data_answer"
    | "general_answer";
  analysis_markdown: string;
  question?: {
    id: string;
    question: string;
    options: { label: string; value: string }[];
  };
  final_hts_code?: string;
  rulings?: { id: string; relevance: string }[];
}

// Unified chat message interface - same for backend and frontend
export interface ChatMessage {
  _id: string;
  content: string;
  source: MessageSender; // Backend uses 'source' field name
  messageOrder: number;
  attachmentIds?: string[];
  createdAt: string;
  finalizeArgs?: FinalizeArgs;
  llmMetadata?: {
    model?: string;
    confidence?: number;
    mcpToolsUsed?: string[];
    hasInternalData?: boolean;
  };
}

// Unified chat interface - same for backend and frontend
export interface Chat {
  _id: string;
  title: string;
  category?: ChatCategory;
  context?: Record<string, unknown>;
  totalMessages?: number;
  firstMessageAt?: string;
  lastActivityAt?: string;
  messages?: ChatMessage[];
  // Set by the agent-chat backend while a response is being produced;
  // cleared on done/error. Lets a client that reconnects mid-stream
  // (refresh, navigate back) discover the streamId and replay buffered
  // events via agentChatStreamReplay.
  activeStreamId?: string | null;
  activeStreamStartedAt?: string | null;
}

export interface ChatResponse {
  data: Chat;
}

export interface ChatsResponse {
  data: Chat[];
  meta: {
    countDocs: number;
    pageNo: number;
    pageSize: number;
  };
}

export interface CreateChatResponse {
  createChat: ChatResponse;
}

export interface GetChatsResponse {
  chats: ChatsResponse;
}

export interface GetChatResponse {
  chat: ChatResponse;
}

export interface SendMessageResponse {
  sendMessage: {
    data: Chat;
  };
}

export interface UpdateChatResponse {
  updateChat: ChatResponse;
}

export interface DeleteChatResponse {
  deleteChat: {
    data: boolean;
  };
}

// Simple types for UI components
export interface UIChatMessage {
  id: string;
  content: string;
  sender: MessageSender;
  timestamp: string;
  messageOrder?: number; // Optional, used for sorting messages in correct order
  attachmentIds?: string[];
  finalizeArgs?: FinalizeArgs;
  llmMetadata?: {
    model?: string;
    confidence?: number;
    mcpToolsUsed?: string[];
    hasInternalData?: boolean;
    thinking?: string[];
    processingSteps?: string[];
    toolCalls?: Array<{
      toolName?: string;
      name?: string;
      arguments?: any;
      args?: any;
      timestamp?: string;
      success?: boolean;
    }>;
  };
}

export interface UIChatSession {
  id: string;
  title: string;
  timestamp: string;
  lastMessage: string;
  messages: UIChatMessage[];
}
